/* assets/ansu-light-layers.js — VERSIE 17
   17: aan-eerst: de lamp gaat al uit zodra er een paar procent van de foto
       in beeld is (ZICHTBAAR_DOOF), dus vrijwel niemand ziet hem uitgaan.
       Bij 85% gaat hij langzaam aan, net als in 16. Productpagina en
       variantwissel: uit zodra de foto in beeld komt, niet pas vlak voor
       het aangaan. Een aan-eerst-kaart gaat pas terug naar aan als hij
       helemaal uit beeld is, zodat je dat terugspringen niet ziet.
   16: aan-eerst: na het snelle uit langzaam weer aan (--ansu-dur-terug).
       Hoort bij CSS 9.
   15: aan-eerst (snippet 8, custom.light_off). Een lamp die al aan staat
       gaat in beeld snel uit, even donker (UIT_PAUZE), en weer aan. Buiten
       beeld gaat een aan-eerst-kaart terug naar aan, zodat hij bij
       terugscrollen opnieuw afspeelt.
   14: variantwissel op de productpagina: de lamp gaat uit en pas weer aan
       als de foto van de nieuwe variant in beeld staat, plus VARIANT_WACHT.
   13: instelbare wachttijd voordat een kaart aangaat (WACHT_AAN) en op de
       productpagina (PDP_WACHT).
   12: kaarten gaan pas aan na het laden + korte pauze (je ziet de overgang
       ook bij kaarten die meteen in beeld zijn). Productpagina: de knop
       loopt niet meer uit de pas na een variantwissel, en een andere
       variant kiezen zet de lamp altijd opnieuw aan.
   11: de kaart gaat aan zodra 85% van de foto in beeld is (was: midden
       van de foto in het middelste deel van het scherm, dat was te laat).
   10: ook op de laptop gaat de collectiekaart aan als hij in beeld komt
       (instelbaar met SCROLL_OP_LAPTOP). Een hele rij gaat van links naar
       rechts na elkaar aan.
   Hoort bij CSS 7 en snippet 7.1.
   9: op de telefoon gaat de collectiekaart pas aan als het midden van de
      foto in het middelste deel van het scherm staat.
   Nieuw: lagen per variant. Op de productpagina kan elke variantfoto een
   eigen wrapper hebben; bij een variantwissel gaat de lamp opnieuw aan op
   de foto van die variant. Werkt ook als Dawn de galerij opnieuw opbouwt. */

window.ANSU_LIGHT_VERSION = 17;

(() => {
  'use strict';

  const kanHoveren = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const rustig = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Warmdraaien via losse Image-objecten. We raken de elementen in de
     pagina bewust niet aan: het `loading`-attribuut van een levend <img>
     wijzigen kan de browser de afbeelding opnieuw laten laden, en dan is
     hij even leeg terwijl de overgang al loopt. */
  function warm(wrap) {
    return Promise.all([...wrap.querySelectorAll('img')].map((el) => new Promise((klaar) => {
      if (el.complete && el.naturalWidth) { klaar(); return; }
      const kopie = new Image();
      if (el.sizes) kopie.sizes = el.sizes;
      if (el.srcset) kopie.srcset = el.srcset;
      kopie.src = el.currentSrc || el.src;
      kopie.decoding = 'async';
      kopie.onload = kopie.onerror = () => klaar();
      setTimeout(klaar, 4000);
    })));
  }

  /* ── speel: de lamp (opnieuw) zien aangaan ─────────────────────────
     Staat de lamp al aan (aan-eerst: de productfoto is de aan-foto), dan
     eerst snel uit, even donker, en daarna met de normale overgang aan.
     Staat hij uit, dan gewoon aan. `magAan` wordt vlak voor het aangaan
     nog gevraagd: is de kaart nog in beeld, staat de knop nog aan? */

  const UIT_PAUZE = 400;   // ms donker tussen snel uit en weer aan

  const cssMs = (naam, standaard) => {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(naam));
    return Number.isFinite(v) ? v : standaard;
  };

  function stopSpelen(wrap) {
    clearTimeout(wrap._ansuSpeel);
    clearTimeout(wrap._ansuTraag);
    wrap.classList.remove('ansu-light-snel', 'ansu-light-traag');
  }

  const isAanEerst = (wrap) => wrap.classList.contains('ansu-light-wrap--aaneerst');

  /* doof: alleen het snelle uit. Het weer aangaan doet speel() later. */
  function doof(wrap) {
    if (rustig) return;
    stopSpelen(wrap);
    wrap.classList.add('is-ready', 'is-warm', 'ansu-light-snel');
    wrap.classList.remove('is-lit');
    wrap._ansuUit = performance.now();
    wrap._ansuSpeel = setTimeout(() => wrap.classList.remove('ansu-light-snel'),
      cssMs('--ansu-snel-uit', 120) + 50);
  }

  function speel(wrap, magAan = () => true) {
    if (rustig) {
      stopSpelen(wrap);
      wrap.classList.add('is-ready', 'is-warm');
      if (magAan()) wrap.classList.add('is-lit');
      return;
    }
    if (wrap.classList.contains('is-lit')) doof(wrap);
    else if (!isAanEerst(wrap) && !wrap._ansuUit) {
      stopSpelen(wrap);
      wrap.classList.add('is-ready', 'is-warm');
      if (magAan()) wrap.classList.add('is-lit');
      return;
    } else {
      clearTimeout(wrap._ansuSpeel);
      clearTimeout(wrap._ansuTraag);
    }
    /* Al eerder gedoofd? Dan alleen de rest van de donkere pauze wachten. */
    const rest = Math.max(0,
      (wrap._ansuUit || 0) + cssMs('--ansu-snel-uit', 120) + UIT_PAUZE - performance.now());
    wrap._ansuSpeel = setTimeout(() => {
      wrap.classList.remove('ansu-light-snel');
      wrap.classList.add('ansu-light-traag');            // langzaam weer aan
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!magAan()) { wrap.classList.remove('ansu-light-traag'); return; }
        wrap.classList.add('is-lit');
        wrap._ansuTraag = setTimeout(() => wrap.classList.remove('ansu-light-traag'),
          cssMs('--ansu-dur-terug', 1800) * 1.2 + 100);
      }));
    }, rest);
  }

  /* ── collectiekaarten warmdraaien ──────────────────────────────────── */

  function warmKaarten(root = document) {
    if (!('IntersectionObserver' in window)) return;
    const wraps = root.querySelectorAll('.ansu-light-wrap:not(.ansu-light-wrap--pdp):not([data-warm])');
    if (!wraps.length) return;

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        io.unobserve(e.target);
        e.target.dataset.warm = '1';
        const el = e.target;
        warm(el).then(() => { el.classList.add('is-warm'); planMeting(); });
      }
    }, { rootMargin: '500px 0px' });

    wraps.forEach((el) => io.observe(el));
  }

  /* ── licht volgt het scrollen ───────────────────────────────────────
     Op de telefoon altijd, op de laptop als SCROLL_OP_LAPTOP aan staat.
     De scrollpositie is het signaal. We kijken hoeveel van de FOTO in
     beeld staat:

       aan  zodra 85% van de foto in beeld is. Je ziet de foto dan vrijwel
            helemaal, ook onderaan het scherm, en ziet hem daarna aangaan.
       uit  zodra minder dan 40% van de foto in beeld is.

     De ruimte tussen aan en uit voorkomt knipperen. Is een foto hoger dan
     het scherm, dan rekenen we tegen de schermhoogte. */

  const SCROLL_OP_LAPTOP = true;   // false = op laptop alleen bij hover, zoals vroeger
  const ZICHTBAAR_AAN = 0.85;      // deel van de foto in beeld → aan (lager = eerder)
  const ZICHTBAAR_UIT = 0.40;      // deel van de foto in beeld → uit
  const ZICHTBAAR_DOOF = 0.02;     // aan-eerst: deel in beeld → al snel uit (0 = bij de eerste pixel)
  const WACHT_AAN = 400;           // ms wachten nadat een kaart in beeld is, voordat hij aangaat

  const volgend = new Set();
  let gepland = false;

  function zetKaart(el, aan, plek = 0) {
    if (aan) {
      el.dataset.zichtbaar = '1';
      if (el.dataset.bezig) return;
      if (el.classList.contains('is-lit') && el.dataset.gespeeld) return;
      const wacht = rustig ? 0 : WACHT_AAN + (el.dataset.gezien ? 0 : Math.min(plek, 4) * 120);
      el.dataset.gezien = '1';
      el.dataset.bezig = '1';
      warm(el).then(() => setTimeout(() => {
        delete el.dataset.bezig;
        if (el.dataset.zichtbaar !== '1') return;
        el.dataset.gespeeld = '1';
        speel(el, () => el.dataset.zichtbaar === '1');
      }, wacht));
    } else {
      el.dataset.zichtbaar = '0';
      /* Aan-eerst die net gedoofd is en nog niet speelde: niets te doen,
         laat het snelle uit gewoon aflopen. */
      if (isAanEerst(el) && !el.dataset.gespeeld && !el.dataset.bezig) return;
      stopSpelen(el);
      delete el.dataset.gespeeld;
      el.classList.remove('is-lit');
    }
  }

  /* Aan-eerst: pas als de kaart helemaal uit beeld is terug naar de
     beginstand (aan), zonder overgang. Zo zie je dat nooit gebeuren, en
     speelt het opnieuw als je terugscrolt. */
  function zetTerug(el) {
    if (!isAanEerst(el) || el.classList.contains('is-lit') || el.dataset.bezig) return;
    stopSpelen(el);
    delete el.dataset.gespeeld;
    el.classList.add('ansu-light-reset', 'is-lit');
    void el.offsetWidth;
    el.classList.remove('ansu-light-reset');
  }

  function meet() {
    gepland = false;
    const h = window.innerHeight || document.documentElement.clientHeight;
    const nieuw = [];
    for (const el of volgend) {
      const r = el.getBoundingClientRect();
      if (!r.height) continue;
      const deel = Math.max(0, Math.min(r.bottom, h) - Math.max(r.top, 0)) / Math.min(r.height, h);

      /* Aan-eerst: zodra er een paar procent in beeld is, al snel uit.
         Werkt ook al tijdens de startpauze. Helemaal uit beeld: terug. */
      if (isAanEerst(el) && !rustig) {
        if (deel <= 0) { zetTerug(el); continue; }
        if (deel >= ZICHTBAAR_DOOF && el.classList.contains('is-lit') && el.classList.contains('is-warm')
            && !el.dataset.gespeeld && !el.dataset.bezig) doof(el);
      }
      if (!gestart) continue;

      if (deel >= ZICHTBAAR_AAN) {
        if ((!el.classList.contains('is-lit') || !el.dataset.gespeeld) && !el.dataset.bezig) nieuw.push([r.top, r.left, el]);
        else el.dataset.zichtbaar = '1';
      } else if (deel <= ZICHTBAAR_UIT) zetKaart(el, false);
    }
    /* Kaarten die tegelijk in beeld komen (een hele rij op de laptop) gaan
       van links naar rechts na elkaar aan, niet allemaal in één klap. */
    nieuw.sort((a, b) => (Math.round(a[0] / 40) - Math.round(b[0] / 40)) || (a[1] - b[1]));
    nieuw.forEach(([, , el], i) => zetKaart(el, true, i));
  }

  /* Pas na het laden van de pagina en een korte pauze gaan kaarten aan.
     Anders staan kaarten die meteen in beeld zijn al aan voordat je kijkt:
     de overgang loopt dan terwijl de pagina nog opbouwt. */
  const START_PAUZE = 700;   // ms na het laden van de pagina
  let gestart = false, startGepland = false;
  const startKaarten = () => {
    if (startGepland) return;
    startGepland = true;
    setTimeout(() => { gestart = true; planMeting(); }, START_PAUZE);
  };
  if (document.readyState === 'complete') startKaarten();
  else window.addEventListener('load', startKaarten, { once: true });
  setTimeout(startKaarten, 2500);   // trage verbinding: niet eindeloos op 'load' wachten

  const planMeting = () => {
    if (gepland) return;
    gepland = true;
    requestAnimationFrame(meet);
  };
  let luistert = false;

  function startAutolight(root = document) {
    if ((kanHoveren && !SCROLL_OP_LAPTOP) || !('IntersectionObserver' in window)) return;
    const kaarten = root.querySelectorAll('.ansu-light-wrap[data-autolight]:not([data-volgt])');
    if (!kaarten.length) return;

    /* Alleen kaarten in of vlak bij beeld meten; de rest kost niets. */
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) volgend.add(e.target);
        else { volgend.delete(e.target); zetKaart(e.target, false); zetTerug(e.target); }
      }
      planMeting();
    }, { rootMargin: '10% 0px' });

    kaarten.forEach((el) => { el.dataset.volgt = '1'; io.observe(el); });

    if (!luistert) {
      luistert = true;
      window.addEventListener('scroll', planMeting, { passive: true });
      window.addEventListener('resize', planMeting, { passive: true });
    }
  }

  /* ── productpagina ─────────────────────────────────────────────────
     Er kan nu per variant een wrapper zijn, elk op de eigen uit-foto in de
     galerij. De knop en de intro gelden voor allemaal tegelijk; bij een
     variantwissel speelt het aangaan opnieuw op de foto van die variant. */

  const PDP_WACHT = 550;       // ms na het laden voordat de lamp op de productpagina aangaat
  const VARIANT_WACHT = 600;   // ms nadat de foto van een andere variant in beeld staat

  const pdpWraps = (root = document) => [...root.querySelectorAll('.ansu-light-wrap--pdp')];
  let pdpAan = false;

  /* De knop volgt altijd de echte stand (pdpAan), ook knoppen die Dawn
     bij een variantwissel opnieuw in de pagina zet. */
  function zetKnoppen() {
    document.querySelectorAll('.ansu-light-toggle').forEach((knop) => {
      knop.setAttribute('aria-pressed', String(pdpAan));
      const label = knop.querySelector('.ansu-light-toggle__label');
      if (label) label.textContent = pdpAan ? knop.dataset.labelOn : knop.dataset.labelOff;
    });
  }

  function zetPdp(aan) {
    pdpAan = aan;
    pdpWraps().forEach((w) => {
      stopWachten(w);
      w.classList.add('is-ready');
      w.classList.toggle('is-lit', aan);
    });
    zetKnoppen();
  }

  const gekozenVariant = () => {
    const el = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return el && el.value;
  };

  const wrapVoor = (id) =>
    (id && document.querySelector(`.ansu-light-wrap--pdp[data-variant-id="${id}"]`)) || null;

  /* Zet één wrapper direct uit (zonder overgang) en laat hem opnieuw
     aangaan zodra hij echt in beeld staat, plus VARIANT_WACHT. Zo zie je
     de lamp aangaan op de foto van de nieuwe variant, ook als de galerij
     eerst nog moet schuiven of door Dawn opnieuw wordt opgebouwd. */
  function stopWachten(wrap) {
    if (wrap._ansuIo) { wrap._ansuIo.disconnect(); wrap._ansuIo = null; }
    clearTimeout(wrap._ansuTimer);
    stopSpelen(wrap);
  }

  function speelAf(wrap) {
    if (!wrap || !pdpAan) return;
    stopWachten(wrap);
    wrap.classList.add('is-ready');

    if (!('IntersectionObserver' in window)) { speel(wrap, () => pdpAan); return; }
    let gedoofd = false;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      /* Brandt hij nog (aan-eerst, of de lamp van de vorige keer)? Dan
         meteen uit zodra de foto in beeld komt, niet pas bij het aangaan. */
      if (!gedoofd && wrap.classList.contains('is-lit')) {
        gedoofd = true;
        warm(wrap).then(() => { if (wrap._ansuIo === io && pdpAan) doof(wrap); });
      }
      if (!entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) return;
      stopWachten(wrap);
      warm(wrap).then(() => {
        wrap.classList.add('is-warm');
        if (!rustig && pdpAan && wrap.classList.contains('is-lit')) doof(wrap);
        wrap._ansuTimer = setTimeout(() => {
          if (pdpAan) speel(wrap, () => pdpAan);
        }, rustig ? 0 : VARIANT_WACHT);
      });
    }, { threshold: [0, 0.5, 1] });
    wrap._ansuIo = io;
    io.observe(wrap);
  }

  /* De lamp gaat vanzelf aan bij binnenkomst, maar pas als de lagen van de
     gekozen variant binnen zijn — anders zie je de overgang niet. */
  function introPdp() {
    const wraps = pdpWraps();
    if (!wraps.length || document.documentElement.dataset.ansuIntro) return;
    document.documentElement.dataset.ansuIntro = '1';

    const eerst = wrapVoor(gekozenVariant()) || wraps[0];
    warm(eerst).then(() => {
      wraps.forEach((w) => w.classList.add('is-ready'));
      eerst.classList.add('is-warm');
      if (rustig) { zetPdp(true); return; }
      pdpAan = true;
      zetKnoppen();
      if (isAanEerst(eerst)) doof(eerst);   // aan-eerst: meteen uit, daarna rustig aan
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setTimeout(() => {
          if (!pdpAan) return;
          wraps.forEach((w) => { if (w !== eerst) w.classList.add('is-lit'); });
          speel(eerst, () => pdpAan);
        }, PDP_WACHT);
      }));
    });
  }

  /* Variantwissel. Oudere Dawn: het verborgen id-veld krijgt een change-
     event en de galerij schuift naar de variantfoto. Dawn 15+: de galerij
     wordt opnieuw opgebouwd; dat vangt de MutationObserver hieronder. */
  function startVariantwissel() {
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t || t.name !== 'id' || !t.value) return;
      if (!pdpWraps().length) return;
      /* Andere variant = de lamp opnieuw zien aangaan, ook als hij uit stond.
         Alle lampen gaan uit en elk gaat weer aan zodra hij in beeld staat. */
      pdpAan = true;
      zetKnoppen();
      pdpWraps().forEach((w) => speelAf(w));
    }, true);
  }

  /* Alleen klikken. Geen hover op de productpagina: de knop staat binnen
     .product-media-container, dus hover zette het licht meteen weer aan
     nadat je hem had uitgeklikt. */
  function startKnop() {
    document.addEventListener('click', (e) => {
      const knop = e.target.closest && e.target.closest('.ansu-light-toggle');
      if (!knop) return;
      e.preventDefault();
      e.stopPropagation();
      zetPdp(!pdpAan);
    });
  }

  /* Nieuw ingevoegde wrappers (Dawn bouwt bij een variantwissel of via
     quick-add stukken pagina opnieuw op) krijgen dezelfde behandeling als
     bij het laden. */
  function startObserver() {
    if (!('MutationObserver' in window)) return;
    new MutationObserver((lijst) => {
      for (const m of lijst) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.matches('.ansu-light-toggle') || n.querySelector('.ansu-light-toggle')) zetKnoppen();
          const wraps = n.matches('.ansu-light-wrap') ? [n] : [...n.querySelectorAll('.ansu-light-wrap')];
          if (!wraps.length) continue;
          warmKaarten(n.parentNode || document);
          startAutolight(n.parentNode || document);
          const pdp = wraps.filter((w) => w.classList.contains('ansu-light-wrap--pdp'));
          if (!pdp.length) continue;
          if (!document.documentElement.dataset.ansuIntro) { introPdp(); continue; }
          pdp.forEach((w) => {
            w.classList.add('is-ready');
            if (pdpAan) speelAf(w);   // gaat aan zodra hij in beeld staat
          });
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── varianten met eigen lagen ─────────────────────────────────────── */

  function startVarianten() {
    const el = document.querySelector('script.ansu-light-map');
    if (!el) return;

    let tabel;
    try { tabel = JSON.parse(el.textContent); } catch { return; }

    const wrap = el.closest('.ansu-light-wrap');
    if (!wrap) return;

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t || t.name !== 'id' || !t.value) return;
      const rij = tabel[String(t.value)];
      if (!rij) return;
      const stel = (sel, url) => {
        const img = wrap.querySelector(sel);
        if (!img) return;
        if (url) { img.removeAttribute('srcset'); img.src = url; img.hidden = false; }
        else { img.hidden = true; }
      };
      stel('.ansu-light--led', rij.led);
      stel('.ansu-light--ambient', rij.ambient);
      stel('.ansu-light--on', rij.aan);
      const basis = wrap.querySelector('.ansu-light-base');
      if (basis && rij.base) { basis.removeAttribute('srcset'); basis.src = rij.base; }
    }, true);
  }

  /* ── opstarten ─────────────────────────────────────────────────────── */

  const start = () => {
    warmKaarten(); startAutolight(); startVarianten(); introPdp(); startObserver();
  };

  startKnop();
  startVariantwissel();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (e) => {
    warmKaarten(e.target);
    startAutolight(e.target);
    if (document.documentElement.dataset.ansuIntro) {
      pdpWraps(e.target).forEach((w) => { w.classList.add('is-ready'); w.classList.toggle('is-lit', pdpAan); });
    } else {
      introPdp();
    }
  });
})();